import { ReflectUtils, HttpResponseError } from "@dooboostore/core";
import { SwcUtils } from "../utils/Utils.js";
import { getParameterMetadata } from "./parameter.js";
const FETCH_METADATA_KEY = Symbol.for("simple-web-component:fetch");
const FETCH_FIELD_FN = Symbol.for("simple-web-component:fetch-field-fn");
const getActiveControllers = (inst) => inst.__swc_fetchControllers ?? (inst.__swc_fetchControllers = /* @__PURE__ */ new Set());
const getLatestControllers = (inst) => inst.__swc_fetchLatest ?? (inst.__swc_fetchLatest = /* @__PURE__ */ new Map());
const abortError = (message) => new DOMException(message, "AbortError");
const combineSignals = (user, own) => {
  if (!user)
    return own;
  if (typeof AbortSignal.any === "function")
    return AbortSignal.any([user, own]);
  const merged = new AbortController();
  for (const s of [user, own]) {
    if (s.aborted) {
      merged.abort(s.reason);
      break;
    }
    s.addEventListener("abort", () => merged.abort(s.reason), { once: true });
  }
  return merged.signal;
};
const never = () => new Promise(() => {
});
const valueCache = /* @__PURE__ */ new WeakMap();
const cacheOf = (inst) => {
  let m = valueCache.get(inst);
  if (!m)
    valueCache.set(inst, m = /* @__PURE__ */ new Map());
  return m;
};
function createFetch(optionsOrUrl) {
  const options = typeof optionsOrUrl === "string" ? { url: optionsOrUrl } : optionsOrUrl;
  return (target, propertyKey, descriptor) => {
    const constructor = target.constructor;
    const isField = !descriptor;
    const list = ReflectUtils.getOwnMetadata(FETCH_METADATA_KEY, constructor) ?? [];
    list.push({ propertyKey, options, field: isField });
    ReflectUtils.defineMetadata(FETCH_METADATA_KEY, list, constructor);
    const extractBody = (v) => {
      const keyToUse = options.valueKey ?? FETCH_METADATA_KEY;
      if (v && typeof v === "object" && keyToUse in v) {
        return v[keyToUse];
      }
      return v;
    };
    const resolveUrl = (inst, helper, args) => {
      const u = options.url;
      return typeof u === "function" ? u(inst, helper, args) : u;
    };
    const isBodyInit = (v) => {
      if (typeof v === "string")
        return true;
      const is = (name) => typeof globalThis[name] === "function" && v instanceof globalThis[name];
      return ArrayBuffer.isView(v) || v instanceof ArrayBuffer || is("Blob") || is("FormData") || is("URLSearchParams") || is("ReadableStream");
    };
    const readBody = async (res) => {
      const text = await res.text();
      if (!text)
        return void 0;
      return (res.headers.get("Content-Type") ?? "").includes("json") ? JSON.parse(text) : text;
    };
    const bodyProcess = options.process;
    const defaultFetching = async (helper, url, init) => {
      const rawBody = init?.body;
      let body = rawBody;
      let contentType;
      if (rawBody !== void 0 && rawBody !== null && !isBodyInit(rawBody)) {
        if (bodyProcess === "json") {
          body = JSON.stringify(rawBody);
          contentType = "application/json";
        } else if (bodyProcess === "form") {
          const fd = new FormData();
          for (const [k, v] of Object.entries(rawBody)) {
            if (v !== void 0 && v !== null)
              fd.append(k, v);
          }
          body = fd;
        } else if (bodyProcess === "urlencoded") {
          body = new URLSearchParams(rawBody).toString();
          contentType = "application/x-www-form-urlencoded";
        } else if (bodyProcess === "text") {
          body = String(rawBody);
          contentType = "text/plain";
        }
      }
      const headers = new Headers(init?.headers);
      if (contentType && !headers.has("Content-Type"))
        headers.set("Content-Type", contentType);
      const w = helper?.$w;
      const fetchFn = typeof w?.fetch === "function" ? w.fetch.bind(w) : globalThis.fetch;
      const res = await fetchFn(url, {
        ...init,
        method: init?.method ?? (body !== void 0 ? "POST" : "GET"),
        headers,
        body
      });
      if (!res.ok) {
        const error = new HttpResponseError(`fetch failed: ${res.status} ${String(url)}`);
        error.response = res;
        error.body = await readBody(res).catch(() => void 0);
        throw error;
      }
      return readBody(res);
    };
    const trigger = options.trigger;
    const isAfter = trigger === "after" || trigger !== "before" && "valueKey" in options;
    const manual = options.manual;
    const begin = (inst, args) => {
      let helper;
      try {
        helper = SwcUtils.getHelperAndHostSet(inst);
      } catch {
        helper = void 0;
      }
      if (inst?.__swc_fetchDetached && inst.isConnected === false)
        return null;
      if (options.filter && !options.filter({ self: inst, helper, args }))
        return null;
      const controller = new AbortController();
      const active = getActiveControllers(inst);
      active.add(controller);
      if (options.abortPrevious) {
        const latest = getLatestControllers(inst);
        latest.get(propertyKey)?.abort(abortError("superseded by a newer call"));
        latest.set(propertyKey, controller);
      }
      const frameworkAborted = () => controller.signal.aborted;
      const cleanup = () => {
        active.delete(controller);
        if (options.abortPrevious && getLatestControllers(inst).get(propertyKey) === controller)
          getLatestControllers(inst).delete(propertyKey);
      };
      const aborted = new Promise((_, reject) => {
        if (controller.signal.aborted)
          reject(controller.signal.reason);
        else
          controller.signal.addEventListener("abort", () => reject(controller.signal.reason), { once: true });
      });
      aborted.catch(() => {
      });
      const doFetch = (init, returnValue) => {
        const pending = Promise.resolve().then(() => manual ? manual(inst, helper, args, controller.signal, returnValue) : defaultFetching(helper, resolveUrl(inst, helper, args), { ...init, signal: combineSignals(init?.signal, controller.signal) }));
        pending.catch(() => {
        });
        return Promise.race([pending, aborted]);
      };
      const fetchBefore2 = () => {
        const reqOpt = manual ? void 0 : options.request;
        return Promise.resolve().then(() => typeof reqOpt === "string" ? { method: reqOpt } : typeof reqOpt === "function" ? reqOpt(inst, helper, args) : void 0).then((init) => doFetch(init));
      };
      const fetchAfter2 = async (returned) => {
        const body = extractBody(returned);
        if (manual)
          return doFetch(void 0, body);
        const reqOpt = options.request;
        const reqInit = typeof reqOpt === "string" ? { method: reqOpt } : typeof reqOpt === "function" ? await reqOpt(inst, helper, args, returned) : void 0;
        return doFetch({ ...reqInit, body: reqInit?.body ?? body });
      };
      const quiet = (run) => {
        const promise = new Promise((resolve, reject) => {
          run.then((v) => {
            if (!frameworkAborted())
              resolve(v);
          }, (e) => {
            if (!frameworkAborted())
              reject(e);
          }).finally(cleanup);
        });
        promise.catch(() => {
        });
        return promise;
      };
      return { frameworkAborted, cleanup, fetchBefore: fetchBefore2, fetchAfter: fetchAfter2, quiet };
    };
    if (isField) {
      const designType = ReflectUtils.getMetadata("design:type", target, propertyKey);
      if (designType !== void 0 && designType !== Function && designType !== Promise && designType !== Object) {
        throw new Error(`[SWC] @fetch field "${String(propertyKey)}" must be a function returning a Promise (\`declare ${String(propertyKey)}: (\u2026) => Promise<T>\`) or a Promise (\`declare ${String(propertyKey)}: Promise<T>\`), not ${designType?.name ?? String(designType)}.`);
      }
      if (Object.prototype.hasOwnProperty.call(target, propertyKey)) {
        throw new Error(`[SWC] "${String(propertyKey)}" is a @fetch field but something else already defines it. Put triggers/outputs on a method that calls this.${String(propertyKey)}().`);
      }
      const remembered = (inst) => {
        const cache = cacheOf(inst);
        const hit = cache.get(propertyKey);
        if (hit)
          return hit;
        const call = begin(inst, []);
        if (!call)
          return never();
        const promise = call.quiet(isAfter ? call.fetchAfter(void 0) : call.fetchBefore());
        cache.set(propertyKey, promise);
        promise.catch(() => {
          if (cache.get(propertyKey) === promise)
            cache.delete(propertyKey);
        });
        return promise;
      };
      const values = /* @__PURE__ */ new WeakMap();
      const get = function() {
        const inst = this;
        let value = values.get(inst);
        if (value)
          return value;
        value = function(...args) {
          const call = begin(inst, args);
          if (!call)
            return never();
          return call.quiet(isAfter ? call.fetchAfter(args[args.length - 1]) : call.fetchBefore());
        };
        value.then = (ok, fail) => remembered(inst).then(ok, fail);
        value.catch = (fail) => remembered(inst).catch(fail);
        value.finally = (done) => remembered(inst).finally(done);
        Object.setPrototypeOf(value, Promise.prototype);
        values.set(inst, value);
        return value;
      };
      get[FETCH_FIELD_FN] = true;
      Object.defineProperty(target, propertyKey, {
        get,
        set(v) {
          const cache = cacheOf(this);
          if (v === void 0 || v === null)
            cache.delete(propertyKey);
          else
            cache.set(propertyKey, Promise.resolve(v));
        },
        configurable: true
      });
      return;
    }
    const originalMethod = descriptor.value;
    let warnedSync = false;
    const isPromiseLike = (v) => v instanceof Promise || v && typeof v.then === "function";
    const guardSync = (raw) => {
      if (!warnedSync && originalMethod?.constructor?.name !== "AsyncFunction" && !isPromiseLike(raw)) {
        warnedSync = true;
        console.warn(`[SWC] @fetch (after) on "${String(propertyKey)}" always returns a Promise: declare the method async (or with an explicit Promise return type) so the signature matches.`);
      }
    };
    const injectPromise = (inst, methodName, args, promise) => {
      const saves = getParameterMetadata(inst, methodName);
      if (!saves.length)
        return args;
      const out = [...args];
      for (const s of saves) {
        if (s.kind === "fetchPromise")
          out[s.index] = promise;
      }
      return out;
    };
    descriptor.value = function(...args) {
      const inst = this;
      const call = begin(inst, args);
      if (!call)
        return void 0;
      if (!isAfter) {
        return originalMethod.apply(inst, injectPromise(inst, propertyKey, args, call.quiet(call.fetchBefore())));
      }
      return (async () => {
        try {
          const raw = originalMethod.apply(inst, args);
          guardSync(raw);
          return await call.fetchAfter(await raw);
        } catch (e) {
          if (call.frameworkAborted())
            return void 0;
          throw e;
        } finally {
          call.cleanup();
        }
      })();
    };
    return descriptor;
  };
}
function fetch(optionsOrUrl) {
  return createFetch(optionsOrUrl);
}
function fetchManual(manual, options) {
  return createFetch({ ...options ?? {}, manual });
}
const beforeMethod = (method) => (url, options) => {
  const { request, ...rest } = options ?? {};
  return createFetch({
    ...rest,
    url,
    trigger: "before",
    request: (c, h, p) => ({ ...request?.(c, h, p) ?? {}, method })
  });
};
const afterMethod = (method) => (url, options) => {
  const { request, ...rest } = options ?? {};
  return createFetch({
    ...rest,
    url,
    trigger: "after",
    request: (c, h, p, returnValue) => ({ ...request?.(c, h, p, returnValue) ?? {}, method })
  });
};
const fetchGet = beforeMethod("GET");
const fetchDelete = beforeMethod("DELETE");
const fetchPost = afterMethod("POST");
const fetchPut = afterMethod("PUT");
const fetchPatch = afterMethod("PATCH");
function fetchLatest(optionsOrUrl) {
  const options = typeof optionsOrUrl === "string" ? { url: optionsOrUrl } : optionsOrUrl;
  return createFetch({ ...options, abortPrevious: true });
}
function fetchBefore(options) {
  return createFetch({ ...options, trigger: "before" });
}
function fetchAfter(options) {
  return createFetch({ ...options, trigger: "after" });
}
const getFetchMetadata = (target) => {
  const constructor = typeof target === "function" ? target : target.constructor;
  return ReflectUtils.findAllMetadata(FETCH_METADATA_KEY, constructor).flat();
};
const refersTo = (v, key) => {
  if (v instanceof Map)
    return v.has(key) || [...v.values()].some((x) => refersTo(x, key));
  if (Array.isArray(v))
    return v.some((x) => refersTo(x, key));
  return !!v && typeof v === "object" && v.propertyKey === key;
};
const findDescriptor = (proto, key) => {
  for (let p = proto; p && p !== Object.prototype; p = Object.getPrototypeOf(p)) {
    const d = Object.getOwnPropertyDescriptor(p, key);
    if (d)
      return d;
  }
  return void 0;
};
const fieldHint = (key) => `Put triggers/outputs on a method that calls this.${String(key)}().`;
const assertFetchFields = (constructor) => {
  const fields = (getFetchMetadata(constructor) ?? []).filter((m) => m.field).map((m) => m.propertyKey);
  for (const key of fields) {
    const d = findDescriptor(constructor.prototype, key);
    if (!d?.value?.[FETCH_FIELD_FN] && !d?.get?.[FETCH_FIELD_FN]) {
      throw new Error(`[SWC] "${String(key)}" is a @fetch field but another decorator replaced it. ${fieldHint(key)}`);
    }
    for (const mk of ReflectUtils.getMetadataKeys(constructor)) {
      if (mk === FETCH_METADATA_KEY || typeof mk === "string")
        continue;
      if (refersTo(ReflectUtils.getMetadata(mk, constructor), key)) {
        const name = typeof mk === "symbol" ? mk.description?.replace("simple-web-component:", "@") : String(mk);
        throw new Error(`[SWC] "${String(key)}" is a @fetch field \u2014 it can't also have ${name}. ${fieldHint(key)}`);
      }
    }
  }
};
class FetchLifeCycler {
  onConnected(helperHostSet) {
    const inst = helperHostSet.$this;
    inst.__swc_fetchDetached = false;
    for (const m of getFetchMetadata(inst) ?? []) {
      if (m.field && Object.prototype.hasOwnProperty.call(inst, m.propertyKey)) {
        throw new Error(`[SWC] @fetch field "${String(m.propertyKey)}" must be declared with \`declare\` (e.g. \`declare ${String(m.propertyKey)}: (\u2026) => Promise<T>\`) \u2014 a plain field shadows the request function.`);
      }
    }
  }
  onDisconnected(helperHostSet) {
    const inst = helperHostSet.$this;
    inst.__swc_fetchDetached = true;
    valueCache.delete(inst);
    const active = inst.__swc_fetchControllers;
    if (!active?.size)
      return;
    for (const c of active)
      c.abort(abortError("element disconnected"));
    active.clear();
  }
}
export {
  FETCH_METADATA_KEY,
  FetchLifeCycler,
  assertFetchFields,
  fetch,
  fetchAfter,
  fetchBefore,
  fetchDelete,
  fetchGet,
  fetchLatest,
  fetchManual,
  fetchPatch,
  fetchPost,
  fetchPut,
  getFetchMetadata
};
//# sourceMappingURL=fetch.js.map
